
import { Component, Watch } from 'vue-property-decorator'
import { deserialise, serialise } from 'kitsu-core'
import { openDialog } from '@/_components/dialog/dialog'

import { Mixins } from 'vue-mixin-decorator'
import MixinInterface from '@/_mixins/index'
import ObservationMixin from '@/_mixins/observation'
import ProjectMixin from '@/_mixins/project'

import ToolbarComponent from '@/_components/bar/appToolbar/appToolbar.vue'
import ProjectPicker from '@/components/Select/Project/Project.vue'

import Editor from '@/components/Editor/Editor.vue'
import EditorViewer from '@/components/EditorViewer/EditorViewer.vue'
import DropZone from '@/components/Picker/DropZone/DropZone.vue'
import Attachment from '@/components/Attachment/Attachment.vue'
import Dialog from '@/components/Dialog/Dialog.vue'
import ExportBtn from '@/views/Project/Observation/components/exportBtn/exportBtn.vue'

import InlineUser from '@/views/User/components/inlineUser/inlineUser.vue'

import ObservationModel from '@/views/Project/Observation/model/observation'
import AttachmentModel from '@/views/Attachment/model/attachment'

import TreeViewPicker from '@/views/Project/Observation/components/observation/treeViewPicker.vue'
import CommentsView from '@/views/Project/Comment/views/Comments/Comments.vue'
import Container from '@/components/Placeholders/Container/Container.vue'
import ContractorPicker from '@/views/Project/Observation/components/contractorPicker.vue'

import StatusDropdown from '@/views/Project/Observation/components/status.vue'
import PrioritySelect from '@/views/Project/Observation/components/priority.vue'
import SeveritySelect from '@/views/Project/Observation/components/severity.vue'

import DateTimeDropdown from '@/views/Project/Observation/components/observation/datetime.vue'
import UserAutocomplete from '@/views/Project/Observation/components/userAutocomplete.vue'

import { observationLimits, observationIcons, dueStatusColor } from '@/types/observation'
import PublishDialog from '@/views/Project/Observation/dialogs/publish.vue'
import RestrictedToIncludeYourselfDialog from '../dialogs/restrictedToIncludeYourself.vue'
import RestrictedToMakePublicDialog from '../dialogs/restrictToMakePublic.vue'
import RestrictedToLabel from '../components/labels/restrictedTo.vue'
import LocationDrawing from '../components/locationDrawing.vue'
import LocationDrawingChip from '@/views/Project/Observation/components/locationDrawingChip.vue'
import SelectedAttachment from '@/views/Project/Observation/components/selectedAttachment.vue'

@Component({
    components: {
        ToolbarComponent,
        ProjectPicker,
        ExportBtn,
        UserAutocomplete,
        TreeViewPicker,
        Editor,
        EditorViewer,
        DropZone,
        Attachment,
        CommentsView,
        Dialog,
        Container,
        InlineUser,
        ContractorPicker,
        StatusDropdown,
        PrioritySelect,
        SeveritySelect,
        DateTimeDropdown,
        PublishDialog,
        RestrictedToLabel,
        LocationDrawing,
        LocationDrawingChip,
        SelectedAttachment,
    },
    mixins: [ObservationMixin],
})
export default class Observation extends Mixins<MixinInterface>(ObservationMixin, ProjectMixin) {
    //CONSTRAINTS
    constraints(key: string, constraint: string) {
        let res = true
        if (
            key in this.observation.constraints &&
            constraint in this.observation.constraints[key]
        ) {
            res = this.observation.constraints[key][constraint]
        }
        return res
    }

    isWritable(key: string) {
        return this.constraints(key, 'writable')
    }

    isAuthorized(key: string) {
        return this.constraints(key, 'authorized')
    }

    isRelevant(key: string) {
        return this.constraints(key, 'relevant')
    }

    // VALUES

    observationIcons = observationIcons
    observationLimits = observationLimits
    dueStatusColor = dueStatusColor

    get categories() {
        const projectId = this.observation?.project?.data?.id
        return projectId
            ? this.$store.getters.observationCategories.filter((item: any) => {
                  return item.project.data?.id == projectId
              })
            : this.$store.getters.observationCategories
    }

    get categoriesTree() {
        return this.unflattenArray(this.categories, undefined)
    }

    get causes() {
        const projectId = this.observation?.project?.data?.id

        return projectId
            ? this.$store.getters.observationCauses.filter((item: any) => {
                  return item.project.data?.id == projectId
              })
            : this.$store.getters.observationCauses
    }

    get causesTree() {
        return this.unflattenArray(Object.values(this.causes), undefined)
    }

    get locations() {
        const projectId = this.observation?.project?.data?.id

        return projectId
            ? this.$store.getters.projectLocations.filter((item: any) => {
                  return item.project.data?.id == projectId
              })
            : this.$store.getters.projectLocations
    }

    get locationsTree() {
        return this.unflattenArray(Object.values(this.locations), undefined)
    }

    get isPrivate() {
        return this.observation?.visibility == 'private' ? true : false
    }

    get isDraft() {
        return this.observation?.visibility == 'private' ? true : false
    }

    get observationId() {
        return this.$route.params?.observationId
    }

    showLog() {
        this.menu = false
        ObservationModel.showLog(new ObservationModel(this.observation))
    }

    // GENERAL
    tools = [
        'header',
        'linkTool',
        'list',
        'embed',
        'quote',
        'table',
        'delimiter',
        'Marker',
        'underline',
    ]

    accessible = true
    menu = false
    closeMenuOnClick = true

    // OBSERVATION
    observationBase: any = null
    observation: any = null
    loadingObservation = false
    loadingAttachmentAsFileObject = false
    uploadingAttachment = false
    editorId = crypto['randomUUID']()
    isRestrictedTo = false

    async goToMenu() {
        this.menu = true
    }

    initializeObservation() {
        this.observation = this.observationBase
            ? JSON.parse(JSON.stringify(this.observationBase))
            : null

        this.observationId ? null : (this.observation.happenedAt = new Date())
        this.isRestrictedTo = this.observation.visibility == 'restricted'
    }

    observationLoadingError = false

    async loadObservation() {
        try {
            this.observationBase = await this.getObservation(this.observationId)
        } catch (error) {
            this.$store.commit('closeSnackbar')
            this.observationLoadingError = true
        }
    }

    async loadObservationTemplate() {
        try {
            const template = await this.getObservationTemplateByProject(this.projectId)
            if (template) {
                this.observationBase = template
            } else {
                this.accessible = false
            }
        } catch (error) {
            this.$store.commit('closeSnackbar')
            this.observationLoadingError = true
        }
    }

    @Watch('projectId')
    async getObservationData() {
        this.loadingObservation = true
        this.newAttachments = []

        if (this.observationId) {
            await this.loadObservation()
        } else {
            await this.loadObservationTemplate()
        }

        this.editorId = crypto['randomUUID']()
        this.initializeObservation()
        this.loadingObservation = false
    }

    setRichText(key: 'description' | 'actionTaken' | 'result', richText: string | null) {
        this.isEqualRichText(richText, this.observationBase[key])
            ? (this.observation[key] = this.observationBase[key])
            : (this.observation[key] = richText)
        if (key == 'description') {
            this.trackErrors('description')
        }

        if (key == 'actionTaken') {
            this.trackErrors('severity')
        }
    }

    get validDescription() {
        return !this.isRichTextEmpty(this.observation.description)
    }

    changeDue = false

    get ageOrClosingTime() {
        let publishedAt: any = this.observation.publishedAt
            ? new Date(this.observation.publishedAt).getTime()
            : null
        let closedAt: any = this.observation.closedAt
            ? new Date(this.observation.closedAt).getTime()
            : new Date().getTime()
        return publishedAt ? closedAt - publishedAt : null
    }

    get project() {
        return this.observation?.project?.data?.id ? this.observation.project.data.id : null
    }

    projectId: any = null

    get timeOpentilClose() {
        return this.timeFrom(
            new Date(this.observation.publishedAt),
            new Date(this.observation.closedAt),
            true
        )
    }

    // ATTACHMENTS
    newAttachments: Array<any> = []
    selectedAttachment: null | Record<string, any> = null

    @Watch('active', { deep: true })
    handleActiveAttachmentUpdate() {
        if (!this.selectedAttachment) return
        if (this.selectedAttachment.id) {
            const index = this.observation.attachments.data.findIndex((attachment: any) => {
                return attachment.id == this.selectedAttachment?.id
            })
            this.observation.attachments.data.splice(index, 1, this.selectedAttachment)
        } else {
            let index = this.newAttachments.findIndex((attachment: any) => {
                return attachment.fileId == this.selectedAttachment?.fileId
            })
            this.newAttachments.splice(index, 1, this.selectedAttachment)
        }
    }

    get attachmentsBase() {
        return this.observationBase.attachments.data ? this.observationBase.attachments.data : []
    }

    get existingAttachments() {
        return this.observation?.attachments?.data ? this.observation.attachments.data : []
    }

    get allAttachments() {
        return [...this.existingAttachments, ...this.newAttachments]
    }

    handleDeletedAttachment(selectedAttachment: Record<string, any>) {
        if (selectedAttachment.id) {
            const index = this.observation.attachments.data.findIndex((attachment: any) => {
                return attachment.id == selectedAttachment.id
            })
            this.observation.attachments.data.splice(index, 1)

            if (this.observation?.mainAttachment?.data?.id == selectedAttachment.id)
                this.setMainAttachment(null)
        } else {
            let index = this.newAttachments.findIndex((attachment: any) => {
                return attachment.fileId == selectedAttachment.fileId
            })
            this.newAttachments.splice(index, 1)
        }
    }

    @Watch('allAttachments')
    setActive() {
        if (this.newAttachments.length > 0) {
            this.selectedAttachment = this.allAttachments[this.allAttachments.length - 1]
            this.focuseOnAttachment()
        } else {
            if (this.observation && this.observation.mainAttachment?.data?.id) {
                this.selectedAttachment = this.allAttachments.find((attachment: any) => {
                    return this.observation.mainAttachment?.data?.id == attachment.id
                })
            } else {
                this.selectedAttachment = this.allAttachments[this.allAttachments.length - 1]
            }
        }
    }

    findAttachmentDifferences(newVal: any, oldVal: any) {
        let result = {}
        const diff = this.difference(newVal, oldVal)

        for (const [key, value] of Object.entries(diff)) {
            if (['description'].includes(key)) {
                result[key] = value
            } else if (['fileId', 'hidden'].includes(key)) {
                result[key] = value
            }
        }

        return result
    }

    get stagedAttachments() {
        const result: Array<Record<string, any>> = []

        if (this.observation.id !== 0) {
            this.existingAttachments.map((attachmen) => {
                let attachmentBase = this.attachmentsBase.find((item) => item.id == attachmen.id)
                const diff = this.findAttachmentDifferences(attachmen, attachmentBase)
                if (!this.lodash.isEmpty(diff)) {
                    result.push({ id: attachmen.id, type: attachmen.type, ...diff })
                }
            })
        }
        return result
    }

    get hasStagedAttachments() {
        return this.stagedAttachments.length > 0
    }

    async manageAttachments() {
        if (this.newAttachments.length > 0) {
            await this.postAttachments()
            this.newAttachments = []
        }

        if (this.hasStagedAttachments) {
            await this.patchAttachments()
        }

        this.observationBase = await this.getObservation(this.observationBase.id)

        this.selectedAttachment = this.observationBase?.attachmen?.data[0]
    }

    // POST
    async postAttachments() {
        for (let index = 0; index < this.newAttachments.length; index++) {
            let data = serialise('attachment', {
                description: this.newAttachments[index].description,
                fileId: this.newAttachments[index].fileId,
                hidden: this.newAttachments[index].hidden,
                observation: {
                    data: {
                        id: this.observationBase.id,
                        type: 'observation',
                    },
                },
            })

            const attachment = await this.postAttachmentObservation(data)

            this.observation.attachments.data.push(attachment)
            this.observationBase.attachments.data.push(attachment)

            if (this.isWritable('mainAttachment')) {
                if (this.observation.mainAttachment?.data?.id) {
                    if (attachment.fileId == this.observation.mainAttachment?.data?.id) {
                        this.observation.mainAttachment = {
                            data: {
                                type: 'attachment',
                                id: attachment.id,
                            },
                        }
                    }
                } else {
                    this.observation.mainAttachment = {
                        data: {
                            type: 'attachment',
                            id: attachment.id,
                        },
                    }
                }
            }
        }
    }

    // PATCH
    async patchAttachments() {
        for (let index = 0; index < this.stagedAttachments.length; index++) {
            const payload = serialise('attachment', {
                ...this.stagedAttachments[index],
            })

            await this.patchObservationAttachment(payload)
        }
    }

    focuseOnAttachment() {
        if (this.selectedAttachment) {
            setTimeout(function () {
                document
                    .getElementById('attachment')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
            }, 200)
        }
    }

    // FILE DROP
    attachmentLoading = false
    counter = 0

    get requirments() {
        return this.$store.getters.attachmentConstraints.maxBytes
            ? this.$store.getters.attachmentConstraints
            : this.getFileRequirements()
    }

    dragenter(event: any) {
        event.preventDefault()
        this.counter++
        if (!event.currentTarget?.classList?.contains('drag-over')) {
            event.currentTarget?.classList?.add('drag-over')
        }
    }

    dragleave(event: any) {
        this.counter--
        if (this.counter === 0) {
            event.currentTarget?.classList?.remove('drag-over')
        }
    }

    async drop(event: any) {
        event.preventDefault()
        event.currentTarget?.classList?.remove('drag-over')
    }

    addAttachmentObj(attachmen: Record<string, any>) {
        let observationAttachment = {
            fileId: attachmen.fileId,
            filename: attachmen.filename,
            description: null,
            hidden: false,
        }

        this.newAttachments.push(observationAttachment)
    }

    writeAttachmentToResource(attachment: AttachmentModel) {
        if (!this.isWritable('attachments')) return

        this.addAttachmentObj(attachment)
    }

    passFiles(file: any) {
        const attachment = file.attributes

        if (this.newAttachments.length == 0 && this.observation?.attachment?.data.length == 0) {
            if (this.isWritable('mainAttachment')) {
                this.observation.mainAttachment = {
                    data: { type: 'file', id: attachment.fileId },
                }
            }
        }

        this.addAttachmentObj(attachment)
    }

    loadingAttachment(data: any) {
        this.attachmentLoading = data
    }

    // MAIN ATTACHMENT
    setMainAttachment(selectedAttachment: Record<string, any> | null) {
        if (!this.isWritable('mainAttachment')) return

        if (selectedAttachment == null) {
            this.observation.mainAttachment = { data: null }
        } else if (selectedAttachment.id) {
            this.observation.mainAttachment = {
                data: selectedAttachment,
            }
        } else {
            this.observation.mainAttachment = {
                data: { type: 'file', id: selectedAttachment.fileId },
            }
        }
    }

    // OBSERVATION CHANGES
    get stagedChanges(): any {
        let diff = this.difference(this.observation, this.observationBase)
        let result = {}

        for (const [key, value] of Object.entries(diff)) {
            if (
                [
                    'category',
                    'cause',
                    'location',
                    'locationDrawing',
                    'responsibleUser',
                    'responsibleLeader',
                    'mainAttachment',
                ].includes(key)
            ) {
                if (this.lodash.isEmpty(this.observationBase[key])) {
                    if (value['data']) {
                        result[key] = value
                    }
                } else {
                    result[key] = value
                }
            } else if (['contractors', 'sharedWith', 'restrictedTo'].includes(key)) {
                result[key] = this.observation[key]
            } else if (['description', 'actionTaken', 'result'].includes(key)) {
                result[key] = value
            } else if (['title'].includes(key)) {
                if (value || Boolean(value) != Boolean(this.observationBase[key])) {
                    result[key] = value ? value : null
                }
            } else if (
                [
                    'status',
                    'priority',
                    'severity',
                    'cost',
                    'archived',
                    'due',
                    'dueWarning',
                    'hasExtendedVisibility',
                    'happenedAt',
                    'latitude',
                    'longitude',
                    'locationDrawingX',
                    'locationDrawingY',
                ].includes(key)
            ) {
                result[key] = value
            }
        }

        return !this.observationId && this.observation.id != 0 ? {} : result
    }

    get hasStagedChanges() {
        return this.observation
            ? !this.isObjectEmpty(this.stagedChanges) ||
                  this.hasStagedAttachments ||
                  this.newAttachments.length > 0
            : false
    }

    get hasContractorsChanges() {
        return this.stagedChanges?.hasOwnProperty('contractors')
    }

    get observationIsValid() {
        return this.validDescription && !this.attachmentLoading
    }

    // DATE AND TIME
    timeZone: any = this.projectTimeZone

    get projectTimeZone() {
        this.timeZone = this.project ? this.project.timeZone : 'Europe/Oslo'
        return this.project ? this.project.timeZone : 'Europe/Oslo'
    }

    get projectOffset() {
        return this.getOffset(this.projectTimeZone)
    }

    get deviceTimeZone() {
        return this.guessTimezone()
    }

    get deviceOffset() {
        return this.getOffset(this.deviceTimeZone)
    }

    get dateProp() {
        return this.formatDate(this.observation.happenedAt, this.timeZone, 'YYYY-MM-DD')
    }

    get timeProp() {
        return this.formatDate(this.observation.happenedAt, this.timeZone, 'HH:mm')
    }

    // PUBLISH OBSERVATION
    async publishObservation() {
        const res = await openDialog(PublishDialog, {
            observation: this.observation,
            handleObservation: true,
            handlePatchObservation: this.handlePatchObservation,
            handlePostObservation: this.handlePostObservation,
        })

        if (res) {
            this.initializeObservation()
            this.$router.push({ name: 'all-observations' })
        }
    }

    // DELETE OBSERVATION
    removeObservationDialog = false
    removeObservationLoading = false
    removeObservationError = false

    async removeObservation() {
        try {
            this.removeObservationLoading = true
            await this.deleteObservation(this.observationBase)
            this.$router.push({ name: 'all-observations' })
        } catch (error) {
            this.removeObservationError = true
        } finally {
            this.removeObservationLoading = false
        }
    }

    // UPDATE OBSERVATION
    loadingUpdate = false

    async updateObservation() {
        if (!this.observationIsValid) return
        try {
            this.loadingUpdate = true
            const observation = await this.handlePatchObservation()

            if (observation) {
                this.observationBase = observation
                this.initializeObservation()
                if (this.to) {
                    this.$router.push(this.to)
                }
            }
        } finally {
            this.loadingUpdate = false
            this.leaveDialog = false
        }
    }

    async handlePatchObservation() {
        if (this.newAttachments || this.hasStagedAttachments) {
            await this.manageAttachments()
        }

        if (!this.isObjectEmpty(this.stagedChanges)) {
            const payload = this.stagedChanges

            // Check if one of locationDrawing properties was updated,
            // if it was updated all 3 should be added to the payload
            if (
                Object.keys(this.stagedChanges).includes('locationDrawingX') ||
                Object.keys(this.stagedChanges).includes('locationDrawingY') ||
                Object.keys(this.stagedChanges).includes('locationDrawing')
            ) {
                payload['locationDrawingX'] = this.observation['locationDrawingX']
                payload['locationDrawingY'] = this.observation['locationDrawingY']
                payload['locationDrawing'] = this.observation['locationDrawing']
            }

            // Handles if observation should be available after user saves it
            // Always true unless user updated restrictedTo and that value is not empty and do not include him
            const keepAccess =
                this.lodash.isArray(payload.restrictedTo?.data) &&
                payload.restrictedTo?.data.length > 0
                    ? payload.restrictedTo?.data.findIndex(
                          (user) => user.id == this.currentUser.id
                      ) > -1
                    : true

            const body = serialise('observation', {
                type: this.observationBase.type,
                id: this.observationBase.id,
                ...payload,
            })

            const observation = await this.patchObservation(body)

            if (keepAccess === false) {
                this.$store.commit('deletedObservation', observation)
                this.$router.push({ name: 'all-observations' })
            }

            return observation
        }
    }

    // RestrictedTo
    get restrictedTo() {
        return this.observation?.restrictedTo?.data
    }

    @Watch('restrictedTo', { deep: true })
    async handleRestrictedTo(newVal, oldVal) {
        if (!this.isRestrictedTo) return
        if (newVal.length == 0) {
            this.toggleRestrictedTo()
            return
        }
        const userHadAccess = oldVal
            ? oldVal.findIndex((user) => user.id == this.currentUser.id) > -1
            : false
        const userGetAccesss = newVal
            ? newVal.findIndex((user) => user.id == this.currentUser.id) > -1
            : false

        if (userHadAccess && !userGetAccesss) {
            // If user updated restrictedTo array and did not include themselves in it
            // Show dialog confirming that user do not want to include themselves in the restricted observation
            this.closeMenuOnClick = false

            const keepAccess = await openDialog(RestrictedToIncludeYourselfDialog, {
                observation: this.observation,
            })

            if (keepAccess === null) {
                return
            } else if (keepAccess) {
                this.observation.restrictedTo.data = oldVal
            }

            await this.$nextTick()
            this.closeMenuOnClick = true
        }
    }

    async toggleRestrictedTo() {
        let newVal = !this.isRestrictedTo
        if (newVal) {
            this.observation.restrictedTo?.data.push(this.observationCurrentDesUser)
            this.isRestrictedTo = newVal
        } else {
            //When user empties restrictedTo, observation becomes available for everyone
            //Show dialog confirming that user want to make observation public
            let makePublic = true
            if (this.observationBase.restrictedTo.data.length > 0) {
                this.closeMenuOnClick = false
                makePublic = await openDialog(RestrictedToMakePublicDialog, {
                    observation: this.observation,
                })
                await this.$nextTick()
                this.closeMenuOnClick = true
            }

            if (!makePublic) {
                this.observation.restrictedTo?.data.push(this.observationCurrentDesUser)
            } else {
                this.isRestrictedTo = newVal
                this.observation.restrictedTo.data = []
            }
        }
    }

    get observationCurrentDesUser() {
        return {
            id: this.currentUserDeserialised.id,
            type: this.currentUserDeserialised.type,
            name: this.currentUserDeserialised.name,

            avatarFileId: this.currentUserDeserialised.avatarFileId,
        }
    }

    // POST OBSERVATION
    loadingPost = false
    postObservationLoading = false
    postObservationError = false

    async createObservation() {
        if (!this.observationIsValid) return

        try {
            this.loadingPost = true
            await this.handlePostObservation()
            this.initializeObservation()
            this.leaveDialog = false
            if (this.to) {
                this.$router.push(this.to)
            } else {
                this.$router.push({ name: 'all-observations' })
            }
        } catch (error) {
            this.postObservationError = true
        } finally {
            this.loadingPost = false
        }
    }

    async handlePostObservation() {
        const body = serialise('observation', {
            type: this.observation.type,
            ...this.stagedChanges,
            project: {
                data: {
                    type: 'project',
                    id: this.projectId,
                },
            },
        })

        delete body.data.relationships.mainAttachment

        this.observationBase = await this.postObservation(body)

        if (this.newAttachments.length > 0 || this.hasStagedAttachments) {
            await this.manageAttachments()
        }

        if (this.observation.mainAttachment.data && this.isWritable('mainAttachment')) {
            const body = serialise('observation', {
                type: this.observationBase.type,
                id: this.observationBase.id,
                mainAttachment: {
                    data: {
                        type: 'attachment',
                        id: this.observation.mainAttachment.data.id,
                    },
                },
            })

            this.observationBase = await this.patchObservation(body)
        }
        return this.observationBase
    }

    // LEAVING GUARD
    leaveDialog = false
    to: any = null

    async beforeRouteLeave(to: any, from: any, next: any) {
        if (this.hasStagedChanges) {
            if (this.to) {
                next()
            } else {
                this.to = to
                this.leaveDialog = true
            }
        } else {
            next()
        }
    }

    closeDialog() {
        this.leaveDialog = false
        this.to = null
    }

    discardChanges() {
        this.initializeObservation()
        this.leaveDialog = false
        this.$router.push(this.to)
    }

    navigateTo(data: any) {
        if (this.hasStagedChanges) {
            this.to = data
            this.leaveDialog = true
        } else {
            this.$router.push(data)
        }
    }

    // STATES
    async created() {
        if (this.observationId) {
            await this.getObservationData()
        }
        window.addEventListener('keydown', this.keyDownHandler)
    }

    destroyed() {
        window.removeEventListener('keydown', this.keyDownHandler)
        this.$store.commit('resetComment')
    }

    keyDownHandler(event: any) {
        if (event.code === 'Escape') {
            this.$router.push({ name: 'all-observations' })
        }
    }

    errorsVisible = false

    errors: Set<string> = new Set([])

    trackErrors(field: string) {
        let requiredDescription = this.$t(
            "fe_observation_errors['observation.required_description']"
        ).toString()

        switch (field) {
            case 'description':
                if (!this.observation.description) {
                    this.errors.add(requiredDescription)
                } else {
                    this.errors.delete(requiredDescription)
                }
                break
        }
    }

    // COMMENTS
    showComment = false

    get commentIsVisible() {
        return this.$vuetify.breakpoint.mdAndUp ? !this.isDraft : !this.isDraft && this.showComment
    }
}
